import React from 'react'
import { Box, Typography, Paper, Table, TableHead, TableRow, TableCell, TableBody, IconButton, Stack, Button, Chip, Dialog, DialogTitle, DialogContent, DialogActions, TablePagination, TableSortLabel } from '@mui/material'
import { Delete as DeleteIcon, Unpublished as UnpublishIcon, Refresh as RefreshIcon, Publish as PublishIcon } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { maybeAttachAuthorization } from '@mahendran/shared-frontend-config'
import { buildApiUrl } from '../lib/apiBase'
import { useToast } from '../hooks/useToast'
import { useToastMessages } from '../utils/toastMessages'

type News = {
  id: number
  title: string
  author?: string
  category?: string
  published?: boolean
  createdAt?: string
  publishedAt?: string | null
  content?: string
  imageUrl?: string
  summary?: string
}

const fetchWithAuth = async (url: string, options: RequestInit = {}) => {
    const headers = new Headers(options.headers);
    const credentials = options.credentials || 'include';
    maybeAttachAuthorization(headers, credentials as RequestCredentials);
    const res = await fetch(url, { ...options, headers, credentials });
    return res;
}

const PostsList: React.FC = () => {
  const { t } = useTranslation()
  const { toastPromise, showError } = useToast()
  const toastMessages = useToastMessages()
  const [rows, setRows] = React.useState<News[]>([])
  const [loading, setLoading] = React.useState(true)
  const [loadError, setLoadError] = React.useState(false)
  const [pending, setPending] = React.useState(false)
  const mutationPending = React.useRef(false)
  const requestId = React.useRef(0)
  const [errorDlg, setErrorDlg] = React.useState<{ open: boolean, message: string }>({ open: false, message: '' })
  const [confirm, setConfirm] = React.useState<{ open: boolean, action: 'publish' | 'unpublish' | 'delete' | null, id: number | null, title?: string }>({ open: false, action: null, id: null })
  const [page, setPage] = React.useState(0)
  const [rowsPerPage, setRowsPerPage] = React.useState(10)
  const [total, setTotal] = React.useState(0)
  const [orderBy, setOrderBy] = React.useState<keyof News | 'createdAt'>('createdAt')
  const [order, setOrder] = React.useState<'asc' | 'desc'>('desc')

  const load = async () => {
    const id = ++requestId.current
    setLoading(true)
    setLoadError(false)
    try {
      const sort = `${orderBy},${order}`
      const url = buildApiUrl(`/news/page?status=all&page=${page}&size=${rowsPerPage}&sort=${encodeURIComponent(sort)}`)
      const res = await fetchWithAuth(url)
      if (!res.ok) {
        let msg = ''
        try { const d = await res.clone().json(); msg = d?.detail || d?.title || '' } catch { /* ignore */ }
        if (!msg) { try { msg = await res.text() } catch { /* ignore */ } }
        throw new Error(msg || `HTTP ${res.status}`)
      }
      const data = await res.json()
      if (id !== requestId.current) return
      setRows(data.content || [])
      setTotal(data.totalElements || 0)
    } catch (e: any) {
      if (id !== requestId.current) return
      setLoadError(true)
      const msg = e?.message || toastMessages.postLoadError()
      showError(msg)
      setErrorDlg({ open: true, message: msg })
    } finally {
      if (id === requestId.current) setLoading(false)
    }
  }

  React.useEffect(() => {
    void load()
    return () => { requestId.current += 1 }
  }, [page, rowsPerPage, orderBy, order])

  const sortBy = (column: typeof orderBy) => {
    setPage(0)
    setOrder(column === orderBy ? (order === 'asc' ? 'desc' : 'asc') : column === 'createdAt' ? 'desc' : 'asc')
    setOrderBy(column)
  }

  const doUnpublish = async (id: number) => {
    const promise = fetchWithAuth(buildApiUrl(`/news/${id}/unpublish`), { method: 'POST' }).then(async (res) => {
      if (!res.ok) throw new Error(await res.text() || `Fehler (${res.status})`)
      return res
    })

    return toastPromise(promise, {
      loading: 'Ziehe zurück...',
      success: () => {
        load()
        return toastMessages.postUnpublished()
      },
      error: (e: any) => {
        const msg = e?.message || toastMessages.postError()
        setErrorDlg({ open: true, message: msg })
        return msg
      },
    }).catch(() => {
      // The error dialog and toast already explain the failed action.
    })
  }

  const doPublish = async (id: number) => {
    const promise = fetchWithAuth(buildApiUrl(`/news/${id}/publish`), { method: 'POST' }).then(async (res) => {
      if (!res.ok) throw new Error(await res.text() || `Fehler (${res.status})`)
      return res
    })

    return toastPromise(promise, {
      loading: 'Veröffentliche...',
      success: () => {
        load()
        return toastMessages.postPublished()
      },
      error: (e: any) => {
        const msg = e?.message || toastMessages.postError()
        setErrorDlg({ open: true, message: msg })
        return msg
      },
    }).catch(() => {
      // The error dialog and toast already explain the failed action.
    })
  }

  const doRemove = async (id: number) => {
    const promise = fetchWithAuth(buildApiUrl(`/news/${id}`), { method: 'DELETE' }).then(async (res) => {
      if (!res.ok) throw new Error(await res.text() || `Fehler (${res.status})`)
      return res
    })

    return toastPromise(promise, {
      loading: 'Lösche...',
      success: () => {
        if (rows.length === 1 && page > 0) setPage(page - 1)
        else void load()
        return toastMessages.postDeleted()
      },
      error: (e: any) => {
        const msg = e?.message || toastMessages.postError()
        setErrorDlg({ open: true, message: msg })
        return msg
      },
    }).catch(() => {
      // The error dialog and toast already explain the failed action.
    })
  }

  const onConfirm = async () => {
    if (confirm.id === null || !confirm.action || mutationPending.current) return
    mutationPending.current = true
    setPending(true)
    const id = confirm.id
    const action = confirm.action
    setConfirm({ open: false, action: null, id: null })
    try {
      if (action === 'publish') await doPublish(id)
      if (action === 'unpublish') await doUnpublish(id)
      if (action === 'delete') await doRemove(id)
    } finally {
      mutationPending.current = false
      setPending(false)
    }
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h4">{t('admin.posts')}</Typography>
        <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading || pending}>{t('btn.refresh')}</Button>
      </Stack>
      {loading && <Typography role="status">Beiträge werden geladen…</Typography>}
      <Paper elevation={3}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sortDirection={orderBy === 'title' ? order : false}>
                <TableSortLabel disabled={pending} active={orderBy === 'title'} direction={orderBy === 'title' ? order : 'asc'} onClick={() => sortBy('title')}>{t('table.title')}</TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'author' ? order : false}>
                <TableSortLabel disabled={pending} active={orderBy === 'author'} direction={orderBy === 'author' ? order : 'asc'} onClick={() => sortBy('author')}>{t('table.author')}</TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'category' ? order : false}>
                <TableSortLabel disabled={pending} active={orderBy === 'category'} direction={orderBy === 'category' ? order : 'asc'} onClick={() => sortBy('category')}>{t('table.category')}</TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'published' ? order : false}>
                <TableSortLabel disabled={pending} active={orderBy === 'published'} direction={orderBy === 'published' ? order : 'asc'} onClick={() => sortBy('published')}>{t('table.published')}</TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'createdAt' ? order : false}>
                <TableSortLabel disabled={pending} active={orderBy === 'createdAt'} direction={orderBy === 'createdAt' ? order : 'desc'} onClick={() => sortBy('createdAt')}>{t('table.created')}</TableSortLabel>
              </TableCell>
              <TableCell align="right">{t('table.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(row => (
              <TableRow key={row.id} hover>
                <TableCell>{row.title}</TableCell>
                <TableCell>{row.author || '-'}</TableCell>
                <TableCell>{row.category || '-'}</TableCell>
                <TableCell>{row.published ? <Chip size="small" color="success" label={t('status.yes')} /> : <Chip size="small" label={t('status.no')} />}</TableCell>
                <TableCell>{new Date(row.createdAt || '').toLocaleString()}</TableCell>
                <TableCell align="right">
                  <IconButton title={t('btn.publish')} onClick={() => setConfirm({ open: true, action: 'publish', id: row.id, title: row.title })} disabled={loading || pending || !!row.published}>
                    <PublishIcon />
                  </IconButton>
                  <IconButton title={t('btn.unpublish')} onClick={() => setConfirm({ open: true, action: 'unpublish', id: row.id, title: row.title })} disabled={loading || pending || !row.published}>
                    <UnpublishIcon />
                  </IconButton>
                  <IconButton disabled={loading || pending} title={t('btn.delete')} onClick={() => setConfirm({ open: true, action: 'delete', id: row.id, title: row.title })}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {!loading && !loadError && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">{t('msg.noneFound')}</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(_, p) => { if (!pending) setPage(p) }}
        rowsPerPage={rowsPerPage}
        SelectProps={{ disabled: pending }}
        onRowsPerPageChange={(e) => { if (pending) return; setRowsPerPage(parseInt(e.target.value, 10)); setPage(0) }}
        rowsPerPageOptions={[5, 10, 25, 50]}
      />
      <Dialog open={errorDlg.open} onClose={() => setErrorDlg({ open: false, message: '' })} maxWidth="sm" fullWidth>
        <DialogTitle>{t('dialog.error')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{errorDlg.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setErrorDlg({ open: false, message: '' })}>{t('btn.ok')}</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={confirm.open} onClose={() => setConfirm({ open: false, action: null, id: null })} maxWidth="xs" fullWidth>
        <DialogTitle>{t('dialog.confirm')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {confirm.action === 'delete' && t('msg.deleteConfirm', { title: confirm.title })}
            {confirm.action === 'publish' && t('msg.publishConfirm', { title: confirm.title })}
            {confirm.action === 'unpublish' && t('msg.unpublishConfirm', { title: confirm.title })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm({ open: false, action: null, id: null })}>{t('btn.cancel')}</Button>
          <Button variant="contained" color={confirm.action === 'delete' ? 'error' : 'primary'} onClick={onConfirm}>{t('btn.ok')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default PostsList